import type { users as UsersNS } from '@cwos/api-client';

/**
 * Pure view logic for Settings → Roles & permissions.
 *
 * Everything here is a rearrangement of the ONE payload `GET /roles/overview`
 * serves — the live catalog, the server-authored metadata, and the roles with
 * their keys. Nothing in this module invents a permission fact: a catalog key
 * the server sent without metadata stays visible (as its raw key + seeded
 * description) rather than being dropped, because hiding a real key would make
 * this screen a second, quietly wrong source of authority.
 */

export interface CatalogEntry {
  key: string;
  /** Server metadata when it exists; the UI renders honestly without it. */
  meta: UsersNS.PermissionMeta | undefined;
  /** The seeded catalog description — the fallback text for a meta-less key. */
  seededDescription: string;
}

/** The full catalog joined to its metadata, in the server's module order. */
export function catalogEntries(overview: UsersNS.RolesOverview): CatalogEntry[] {
  const entries = overview.permissions.map((p) => ({
    key: p.key,
    meta: overview.metadata[p.key],
    seededDescription: p.description,
  }));
  return sortByModule(entries, overview.modules);
}

/** Group entries by module. Keys without metadata group under `Other`. */
export function groupByModule(
  entries: CatalogEntry[],
  modules: string[],
): { module: string; entries: CatalogEntry[] }[] {
  const byModule = new Map<string, CatalogEntry[]>();
  for (const entry of entries) {
    const module = entry.meta?.module ?? 'Other';
    const list = byModule.get(module) ?? [];
    list.push(entry);
    byModule.set(module, list);
  }
  const order = [...modules, 'Other'];
  return order
    .filter((m) => byModule.has(m))
    .map((m) => ({ module: m, entries: byModule.get(m)! }));
}

function sortByModule(entries: CatalogEntry[], modules: string[]): CatalogEntry[] {
  const rank = (e: CatalogEntry) => {
    const i = e.meta ? modules.indexOf(e.meta.module) : -1;
    return i === -1 ? modules.length : i;
  };
  return [...entries].sort((a, b) => {
    const mi = rank(a) - rank(b);
    if (mi !== 0) return mi;
    const an = a.meta?.name ?? a.key;
    const bn = b.meta?.name ?? b.key;
    return an.localeCompare(bn);
  });
}

/**
 * Search across human names, descriptions, AND internal keys.
 *
 * The internal key is searchable even though it renders only inside the
 * advanced disclosure: an administrator holding a key name from an error
 * message or an audit row must be able to find what it means.
 */
export function searchEntries(entries: CatalogEntry[], query: string): CatalogEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) return entries;
  return entries.filter((e) => {
    const meta = e.meta;
    return (
      e.key.toLowerCase().includes(q) ||
      e.seededDescription.toLowerCase().includes(q) ||
      (meta !== undefined &&
        (meta.name.toLowerCase().includes(q) ||
          meta.description.toLowerCase().includes(q) ||
          meta.module.toLowerCase().includes(q)))
    );
  });
}

/**
 * Side-by-side capability diff of two roles.
 *
 * `onlyA`/`onlyB` are what separates the roles; `shared` is what they agree
 * on. Computed from the roles' permission key lists exactly as the server
 * serves them — no inference from role names.
 */
export interface RoleDiff {
  onlyA: string[];
  onlyB: string[];
  shared: string[];
}

export function diffRoles(aKeys: readonly string[], bKeys: readonly string[]): RoleDiff {
  const a = new Set(aKeys);
  const b = new Set(bKeys);
  return {
    onlyA: [...a].filter((k) => !b.has(k)).sort(),
    onlyB: [...b].filter((k) => !a.has(k)).sort(),
    shared: [...a].filter((k) => b.has(k)).sort(),
  };
}

/** The badge for a sensitivity tier; standard keys carry none. */
export function sensitivityBadge(
  sensitivity: UsersNS.PermissionSensitivity,
): { label: string; variant: 'look' | 'urgent' } | null {
  if (sensitivity === 'high') return { label: 'High sensitivity', variant: 'urgent' };
  if (sensitivity === 'elevated') return { label: 'Elevated', variant: 'look' };
  return null;
}
